import {
  postWebViewMessage,
  WebViewMessageType,
} from "@/apps/util/hooks/useWebViewMessage"
import useIsWebView from "@/product/util/hook/useIsWebView"
import Modal, { ModalProps } from "@components/modal/Modal"
import {
  DiscoButton,
  DiscoButtonProps,
  DiscoIcon,
  DiscoIconKinds,
  DiscoText,
  DiscoThemeProvider,
} from "@disco-ui"
import { useTheme } from "@material-ui/core"
import { ClassNameMap } from "@material-ui/core/styles/withStyles"
import classNames from "classnames"
import { useEffect } from "react"
import classes from "./DiscoWarningModal.module.css"

export interface WarningModalProps extends ModalProps {
  title: string
  /** Default is 'danger' */
  variant?: DiscoWarningModalVariant
  description: React.ReactNode
  body?: React.ReactNode
  classes?: Partial<ClassNameMap<"buttonsWrapper" | "title">>
  testid: string
  customConfirmationButton?: React.ReactNode
  confirmationButtonProps?: DiscoButtonProps
  cancelButtonText?: string | null
  shouldDisplayCancelSpinner?: boolean
  icon?: DiscoIconKinds
  hideIcon?: boolean
  onCancel?: () => void
  alignTitle?: "left" | "center" | "right"
}

type DiscoWarningModalVariant = "error" | "warning" | "primary"

function DiscoWarningModal({
  isOpen,
  onClose,
  title,
  variant = "error",
  description,
  body,
  testid,
  classes: customClasses,
  customConfirmationButton,
  confirmationButtonProps,
  parentSelector,
  modalContentLabel,
  /** Default is 'Cancel' */
  cancelButtonText = "Cancel",
  shouldDisplayCancelSpinner = false,
  icon,
  hideIcon = false,
  onCancel,
  alignTitle = "center",
}: WarningModalProps) {
  const appTheme = useTheme()
  const isWebView = useIsWebView()

  // Trigger shadow on mobile header
  useEffect(() => {
    if (!isWebView) return

    postWebViewMessage({
      type: WebViewMessageType.toggle_disco_modal,
      value: isOpen,
    })

    return () => {
      postWebViewMessage({
        type: WebViewMessageType.toggle_disco_modal,
        value: false,
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  function getIcon(): { icon: DiscoIconKinds; iconColor: string } {
    switch (variant) {
      // Updated colours: https://www.figma.com/design/pwkwd5lqCdHWLXmWQgh1V5/Groovy---02---Application?node-id=8475-9954&m=dev
      case "primary":
        return { icon: icon || "info", iconColor: "var(--groovy-primary-main)" }
      case "error":
        return { icon: "warning", iconColor: "var(--groovy-red-300)" }
      case "warning":
        return { icon: "warning", iconColor: "var(--groovy-yello-300)" }
    }
  }

  return (
    <DiscoThemeProvider theme={appTheme}>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        parentSelector={parentSelector}
        customClassName={classNames(classes.container, customClasses?.title)}
        modalContentLabel={modalContentLabel}
      >
        <div
          className={classNames(classes.header, {
            [classes.alignCenter]: alignTitle === "center",
            [classes.alignLeft]: alignTitle === "left",
            [classes.alignRight]: alignTitle === "right",
          })}
        >
          {!hideIcon && (
            <div
              className={classNames(classes.iconWrapper, {
                [classes.iconWrapperPrimary]: variant === "primary",
                [classes.iconWrapperWarning]: variant === "warning",
                [classes.iconWrapperError]: variant === "error",
              })}
            >
              <DiscoIcon
                icon={getIcon().icon}
                color={getIcon().iconColor}
                className={classes.icon}
              />
            </div>
          )}
          <DiscoText variant={"heading-sm"} data-testid={`${testid}.title`}>
            {title}
          </DiscoText>
        </div>
        <div className={classes.body}>
          <DiscoText data-testid={`${testid}.description`}>{description}</DiscoText>
          {body}
          <div
            className={classNames(classes.buttonsWrapper, customClasses?.buttonsWrapper)}
          >
            {cancelButtonText !== null && (
              <DiscoButton
                data-testid={`${testid}.cancel-button`}
                width={"100%"}
                color={"grey"}
                variant={"outlined"}
                onClick={onCancel || onClose}
                shouldDisplaySpinner={shouldDisplayCancelSpinner}
                disabled={shouldDisplayCancelSpinner}
              >
                {cancelButtonText}
              </DiscoButton>
            )}
            {customConfirmationButton ||
              (confirmationButtonProps && (
                <DiscoButton
                  // The button color changes only when the variant is danger
                  // https://www.figma.com/design/pwkwd5lqCdHWLXmWQgh1V5/Groovy---02---Application?node-id=8475-9954&t=FIy1kLEs4VSJQ0na-4
                  color={variant === "error" ? "error" : "primary"}
                  width={"100%"}
                  {...confirmationButtonProps}
                  disabled={
                    confirmationButtonProps?.shouldDisplaySpinner ||
                    confirmationButtonProps?.disabled
                  }
                  data-testid={`${testid}.confirm-button`}
                >
                  {confirmationButtonProps?.children}
                </DiscoButton>
              ))}
          </div>
        </div>
      </Modal>
    </DiscoThemeProvider>
  )
}

export default DiscoWarningModal
